import { useEffect, useRef } from "react";
import { User } from "lucide-react";

interface VideoPlayerProps {
  stream?: MediaStream;
  isLoading: boolean;
  isVideoEnabled: boolean;
  previewUrl?: string;
  avatarName: string;
  isFullscreen: boolean;
  videoContainerRef: React.RefObject<HTMLDivElement | null>;
  children?: React.ReactNode;
}

export default function VideoPlayer({
  stream,
  isLoading,
  isVideoEnabled,
  previewUrl,
  avatarName,
  isFullscreen,
  videoContainerRef,
  children,
}: VideoPlayerProps) {
  const mediaStream = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    if (stream && mediaStream.current) {
      mediaStream.current.srcObject = stream;
      mediaStream.current.onloadedmetadata = () => {
        mediaStream.current!.play();
      };
    }
  }, [stream]);

  return (
    <div className="card bg-base-100 shadow-xl">
      <div className="card-body">
        <div
          ref={videoContainerRef}
          className={`relative flex justify-center bg-black rounded-lg overflow-hidden ${
            isFullscreen ? "h-full" : ""
          }`}
        >
          {stream ? (
            <>
              <video
                ref={mediaStream}
                autoPlay
                playsInline
                className={
                  isFullscreen
                    ? "w-full h-full object-contain"
                    : "w-full h-auto max-h-[600px]"
                }
                style={{ display: isVideoEnabled ? "block" : "none" }}
              >
                <track kind="captions" />
              </video>

              {!isVideoEnabled && (
                <div className="flex items-center justify-center w-full h-[600px] bg-gradient-to-br from-gray-800 to-gray-900">
                  <User className="w-48 h-48 text-gray-600" strokeWidth={1} />
                </div>
              )}

              {/* Floating Controls - passed as children */}
              {children}
            </>
          ) : previewUrl ? (
            <div className="relative flex items-center justify-center w-full h-[400px]">
              <img
                src={previewUrl}
                alt={`${avatarName} preview`}
                className="w-full h-full object-cover"
              />
              {!isLoading && (
                <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                  <div className="text-center text-white">
                    <p className="text-xl font-semibold">
                      Chat with {avatarName}?
                    </p>
                  </div>
                </div>
              )}
              {isLoading && (
                <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                  <div className="text-center text-white">
                    <span className="loading loading-spinner loading-lg"></span>
                    <p className="mt-4">Initializing avatar...</p>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="flex items-center justify-center w-full h-[400px] text-white">
              {isLoading ? (
                <div className="text-center">
                  <span className="loading loading-spinner loading-lg"></span>
                  <p className="mt-4">Initializing avatar...</p>
                </div>
              ) : (
                <div className="text-center">
                  <p className="text-lg">Avatar video will appear here</p>
                  <p className="text-sm mt-2">
                    Start a session to interact with the avatar
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
